import React from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { SkeletonRounded, SkeletonText } from '@/components/common/Skeleton'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { FormField } from '@/components/forms/FormField'
import { FormTextarea } from '@/components/forms/FormTextarea'
import { FormSelect } from '@/components/forms/FormSelect'
import { useDepartments, useEmployees } from '@/hooks/useWorkforce'
import { useCreateDepartment, useUpdateDepartment } from '@/hooks/useWorkforceMutations'
import { departmentSchema, DepartmentFormData, cleanFormData } from '@/lib/validations/workforce'
import type { Department } from '@/types/workforce'
import { Building2, Save, X } from 'lucide-react'

interface DepartmentFormProps {
  department?: Department
  mode: 'create' | 'edit'
}

export function DepartmentForm({ department, mode }: DepartmentFormProps) {
  const navigate = useNavigate()

  // ✨ React Hook Form with Zod validation
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
  } = useForm<DepartmentFormData>({
    resolver: zodResolver(departmentSchema),
    mode: 'onBlur', // Validate on blur for better UX
    defaultValues: {
      name: department?.name ?? '',
      description: department?.description ?? '',
      costCenter: department?.costCenter ?? '',
      budget: department?.budget,
      managerId: department?.managerId ?? '',
      parentDepartmentId: department?.parentDepartmentId ?? '',
    },
  })

  // Server state
  const { departments, loading: depsLoading } = useDepartments()
  const { employees, loading: empsLoading } = useEmployees()

  // Mutations
  const createDepartment = useCreateDepartment()
  const updateDepartment = useUpdateDepartment()

  const isLoading = isSubmitting || createDepartment.isPending || updateDepartment.isPending

  // Form submission
  const onSubmit = async (data: DepartmentFormData) => {
    try {
      const cleanedData = cleanFormData(data)

      if (mode === 'create') {
        await createDepartment.mutateAsync(cleanedData)
        navigate({ to: '/workforce/departments' })
      } else if (department) {
        await updateDepartment.mutateAsync({
          departmentId: department.departmentId,
          updates: cleanedData,
        })
        navigate({ to: `/workforce/departments/${department.departmentId}` })
      }
    } catch (error) {
      console.error('Form submission error:', error)
    }
  }

  const handleCancel = () => {
    if (mode === 'edit' && department) {
      navigate({ to: `/workforce/departments/${department.departmentId}` })
    } else {
      navigate({ to: '/workforce/departments' })
    }
  }

  // Filter out current department from parent options (prevent circular reference)
  const parentDepartmentOptions = departments
    .filter((dept) => dept?.departmentId !== department?.departmentId && dept?.name)
    .map((dept) => ({ value: dept?.departmentId || '', label: dept?.name || '' }))

  // Filter active employees for manager selection
  const managerOptions = employees
    .filter((emp) => emp?.status === 'ACTIVE' && emp?.personalInfo?.name)
    .map((emp) => ({
      value: emp.employeeId,
      label: `${emp.personalInfo.name}${emp.currentEngagement?.roleName ? ` - ${emp.currentEngagement.roleName}` : ''}`,
    }))

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <Card className="p-6">
        <h2 className="font-display text-xl font-semibold text-white mb-6 flex items-center gap-2">
          <Building2 className="w-5 h-5 text-brand-primary" />
          Informações Básicas
        </h2>

        <div className="space-y-4">
          {/* Department Name */}
          <FormField
            label="Nome do Departamento"
            name="name"
            register={register}
            error={errors.name}
            required
            placeholder="Ex: Tecnologia, Recursos Humanos, Vendas"
          />

          {/* Description */}
          <FormTextarea
            label="Descrição"
            name="description"
            register={register}
            error={errors.description}
            placeholder="Descrição do departamento e suas responsabilidades..."
            rows={3}
          />

          {/* Cost Center */}
          <FormField
            label="Centro de Custo"
            name="costCenter"
            register={register}
            error={errors.costCenter}
            placeholder="Ex: CC100, TECH01"
            helperText="Alfanumérico, 5-20 caracteres. Sem espaços ou caracteres especiais."
            onChange={(value) => value.replace(/[^a-zA-Z0-9]/g, '')}
          />

          {/* Budget */}
          <FormField
            label="Orçamento (R$)"
            name="budget"
            type="number"
            register={register}
            error={errors.budget}
            placeholder="0.00"
            step={0.01}
            min={0}
            max={1000000000}
          />
        </div>
      </Card>

      <Card className="p-6">
        <h2 className="font-display text-xl font-semibold text-white mb-6">
          Estrutura Organizacional
        </h2>

        <div className="space-y-4">
          {/* Parent Department */}
          {depsLoading ? (
            <div className="space-y-2">
              <SkeletonText width="40%" height={14} />
              <SkeletonRounded width="100%" height={40} />
            </div>
          ) : (
            <FormSelect
              label="Departamento Pai"
              name="parentDepartmentId"
              register={register}
              error={errors.parentDepartmentId}
              options={parentDepartmentOptions}
              placeholder="Nenhum (departamento raiz)"
            />
          )}

          {/* Manager */}
          {empsLoading ? (
            <div className="space-y-2">
              <SkeletonText width="40%" height={14} />
              <SkeletonRounded width="100%" height={40} />
            </div>
          ) : (
            <FormSelect
              label="Gerente do Departamento"
              name="managerId"
              register={register}
              error={errors.managerId}
              options={managerOptions}
              placeholder="Selecione um gerente"
            />
          )}
        </div>
      </Card>

      {/* Form Actions */}
      <div className="flex items-center justify-end gap-3">
        <Button type="button" variant="ghost" onClick={handleCancel} disabled={isLoading}>
          <X className="w-4 h-4 mr-2" />
          Cancelar
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? (
            <>
              <LoadingSpinner size="sm" className="mr-2" />
              Salvando...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {mode === 'create' ? 'Criar Departamento' : 'Salvar Alterações'}
            </>
          )}
        </Button>
      </div>
    </form>
  )
}
